<template>
  <div class="quarter-picker" v-outside:click="{ exclude: ['popover'], handler: closePicker }" @click="openPicker">
    <!-- <label class="sr-only" id="accessible-active-descendant" for="quarter-picker-input">{{ accessibleLabel }}</label> -->
    <el-input
      v-popover:popover
      id="quarter-picker-input"
      class="el-date-editor bg-white dark:bg-darkblue-900"
      :value="valueLabel"
      aria-hidden
      tabindex="-1"
      :clearable="clearable"
      :loading="loading"
      :disabled="disabled"
      @focus="moveFocusToAccessibleInput"
    >
      <template #prefix>
        <i class="el-input__icon el-icon-date"></i>
      </template>
    </el-input>
    <input
      type="text"
      ref="accessibleInput"
      class="sr-only"
      :value="accessibleLabel"
      @focus="handleInputFocus"
      @blur="handleInputBlur"
      @keydown="handleInputKeydown"
    >
    <el-popover
      id="popover-trimestres"
      aria-labelledby="quarter-picker-input"
      ref="popover"
      v-model="isOpen"
      :tabindex="-1"
      @show="handlePopoverShow"
      placement="bottom-start"
      :popper-class="popperClassComputed"
      width="auto"
      :append-to-body="false"
      trigger="manual"
    >
      <template>
        <div class="el-picker-panel__body-wrapper" tabindex="-1" @mousedown.prevent.stop>
          <div class="el-picker-panel__body">
            <div class="el-date-picker__header el-date-picker__header--bordered">
              <button type="button" :tabindex="-1" :aria-label="$t('components.datetime_picker.prev_year')" :aria-hidden="ariaHidden" class="el-picker-panel__icon-btn el-date-picker__prev-btn el-icon-d-arrow-left" @click="goPrevPage()"></button>
              <span role="button" :tabindex="-1" :aria-hidden="ariaHidden" class="el-date-picker__header-label" @click="view = 'year'">{{ currentYearLabel }}</span>
              <button type="button" :tabindex="-1" :aria-label="$t('components.datetime_picker.next_year')" :aria-hidden="ariaHidden" class="el-picker-panel__icon-btn el-date-picker__next-btn el-icon-d-arrow-right" @click="goNextPage()"></button>
            </div>
            <div class="el-picker-panel__content">
              <table v-if="view === 'year'" class="el-year-table">
                <tbody>
                  <tr v-for="(chunk, rowIndex) in chunkedYears" :key="rowIndex">
                    <td v-for="(year, colIndex) in chunk" :key="colIndex" :aria-selected="isCurrentYear(year)" :class="{ available: year !== null, current: isCurrentYear(year), today: isTodayYear(year), disabled: isDisabledYear(year) }">
                      <a v-if="year !== null" class="cell" :id="getYearCellId(year)" :aria-label="year.year()" :aria-hidden="ariaHidden" @click="selectYear(year)">{{ $d(year, 'year_date') }}</a>
                    </td>
                  </tr>
                </tbody>
              </table>
              <table v-else class="el-month-table el-date-table is-week-mode" style="">
                <tbody>
                  <tr v-for="(chunk, rowIndex) in chunkedMonths" :key="rowIndex" class="el-date-table__row" :id="getQuarterId(chunk[0])" :aria-label="getAccessibleQuarterLabel(chunk[0])" :aria-selected="isCurrentQuarter(chunk)" :aria-hidden="ariaHidden" :class="{ current: isCurrentQuarter(chunk) }">
                    <td v-for="(month, colIndex) in chunk" :key="colIndex" :class="{ available: month !== null, today: isTodayMonth(month), disabled: isDisabledMonth(month), 'in-range': isInRange(month), 'start-date': isStartDate(month), 'end-date': isEndDate(month) }">
                      <div v-if="month !== null"><a class="cell" :aria-label="month.locale($i18n.locale).format('MMMM YYYY')" @click="selectQuarterByMonth(month)">{{ $d(month, 'only_month_date') }}</a></div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </template>
    </el-popover>
    <util-keys @keyup="handlePickerKeyup" />
  </div>
</template>

<script>
import moment from 'moment'
import KeyCodes from '@/constants/KeyCodes'
import UtilKeys from '@/components/utilities/UtilKeys'
export default {
  name: 'quarter-picker',
  components: {
    UtilKeys
  },
  props: {
    value: {
      type: [String, Number, Date],
      default: null
    },
    format: {
      type: String,
      default: '[Q]Q YYYY'
    },
    valueFormat: {
      type: String,
      default: '[Q]Q YYYY'
    },
    clearable: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    popperClass: {
      type: String,
      default: ''
    },
    disabledDate: {
      type: Function,
      default: null
    }
  },
  data () {
    return {
      moment,
      today: moment().startOf('day'),
      currentDate: null,
      view: 'quarter',
      isOpen: false,
      yearRange: 10,
      chunkSize: 3,
      lastAccessibleMessage: null,
      ariaHidden: true
    }
  },
  mounted () {
    this.currentDate = this.selectedDate ? this.selectedDate.clone() : this.today.clone().startOf('quarter')
  },
  computed: {
    popperClassComputed () {
      return ['el-picker-panel', 'el-date-picker', 'el-date-picker--quarter', this.popperClass].join(' ')
    },
    selectedDate: {
      get () {
        return this.value ? moment(this.value, this.valueFormat) : null
      },
      set (newValue) {
        if (newValue) {
          this.$emit('input', this.valueFormat ? moment(newValue).format(this.valueFormat) : moment(newValue).toDate())
        } else {
          this.$emit('input', null)
        }
      }
    },
    currentYear () {
      const currentDate = this.currentDate || this.today
      return currentDate.year() ?? null
    },
    rangeStartYear () {
      return Math.floor(this.currentYear / 10) * 10
    },
    rangeEndYear () {
      return this.rangeStartYear + this.yearRange - 1
    },
    currentYearLabel () {
      return this.view === 'year' ? `${this.rangeStartYear} - ${this.rangeEndYear}` : this.currentYear
    },
    valueLabel () {
      return this.selectedDate?.format(this.format) ?? ''
    },
    rangeYears () {
      const years = []
      for (let i = this.rangeStartYear; i <= this.rangeEndYear; i++) {
        years.push(moment().year(i).startOf('year'))
      }
      return years
    },
    chunkedYears () {
      return this.chunkArray(this.rangeYears, this.chunkSize)
    },
    rangeMonths () {
      const months = []
      const currentDate = this.currentDate || this.today
      const current = currentDate.clone().startOf('year')
      for (let i = 0; i < 12; i++) {
        months.push(current.clone().month(i).startOf('month'))
      }
      return months
    },
    chunkedMonths () {
      return this.chunkArray(this.rangeMonths, this.chunkSize)
    },
    accessibleLabel () {
      return (this.currentDate && (this.view === 'year' ? this.currentYear : this.getAccessibleQuarterLabel(this.currentDate))) || ''
    }
  },
  methods: {
    moveFocusToAccessibleInput () {
      if (this.$refs.accessibleInput) {
        this.$refs.accessibleInput.focus()
      }
    },
    announceAccessible (message) {
      if (!message || this.lastAccessibleMessage === message) return
      this.lastAccessibleMessage = message

      if (typeof this.doFeedback === 'function') {
        this.doFeedback(message, 'info', 4000, true)
      }
    },
    getAccessibleQuarterLabel (date) {
      const target = moment.isMoment(date) ? date.clone() : moment(date)
      return this.$tc('components.datetime_picker.quarter_accessible_label', target.quarter(), {
        year: target.year()
      })
    },
    announceActiveItem () {
      if (!this.isOpen) return

      if (this.view === 'year') {
        this.announceAccessible(this.currentYear)
        return
      }

      this.announceAccessible(`${this.getAccessibleQuarterLabel(this.currentDate)}.`)
    },
    announceCurrentContext () {
      if (!this.isOpen) return

      if (this.view === 'year') {
        this.announceAccessible(this.$t('aria.quarter_picker.year_view_context', {
          startYear: this.rangeStartYear,
          endYear: this.rangeEndYear,
          activeYear: this.currentYear
        }))
        return
      }

      this.announceAccessible(`${this.getAccessibleQuarterLabel(this.currentDate)}.`)
    },
    getYearCellId (year) {
      if (!year || !moment.isMoment(year)) return null
      return `year-cell-${year.year()}`
    },
    getQuarterId (quarter) {
      if (!quarter || !moment.isMoment(quarter)) return null
      return `quarter-cell-${quarter.year()}-${quarter.quarter()}`
    },
    handlePopoverShow () {
      this.applyPopoverTabIndex()
    },
    applyPopoverTabIndex () {
      this.$nextTick(() => {
        const popover = this.$refs.popover
        const popoverEl = popover?.popperElm || popover?.$refs?.popper
        const referenceEl = popover?.referenceElm || popover?.$refs?.reference

        if (popoverEl) {
          popoverEl.setAttribute('tabindex', -1)
          // popoverEl.setAttribute('aria-hidden', false)
        }

        if (referenceEl) {
          referenceEl.setAttribute('tabindex', -1)
        }
      })
    },
    handleInputFocus () {
      this.openPicker()
    },
    handleInputBlur () {
      // El cierre se controla con Tab para no cerrar al perder foco por DevTools u otros cambios de contexto.
    },
    blurInput () {
      const inputEl = this.$el?.querySelector('input')
      if (inputEl && typeof inputEl.blur === 'function') {
        inputEl.blur()
      }
    },
    handleInputKeydown (event) {
      const key = event.key

      if (key === KeyCodes.TAB) {
        if (this.isOpen) {
          this.closePicker()
          // this.blurInput()
          // event.stopPropagation()
        }
        return
      }

      const keysHandledByPicker = [
        KeyCodes.ESC,
        KeyCodes.HOME,
        KeyCodes.PAGE_UP,
        KeyCodes.PAGE_DOWN,
        KeyCodes.ARROW_LEFT,
        KeyCodes.ARROW_RIGHT,
        KeyCodes.ARROW_UP,
        KeyCodes.ARROW_DOWN,
        KeyCodes.ENTER
      ]

      if (keysHandledByPicker.includes(key)) {
        event.preventDefault()
      }
    },
    handlePickerKeyup (event) {
      switch (event.key) {
        case KeyCodes.HOME:
          this.view = 'year'
          this.announceCurrentContext()
          event.preventDefault()
          event.stopPropagation()
          break
        case KeyCodes.ESC:
          this.closePicker()
          event.preventDefault()
          break
        case KeyCodes.PAGE_UP:
          this.doPrevPage(true)
          // this.announceCurrentContext()
          event.preventDefault()
          break
        case KeyCodes.PAGE_DOWN:
          this.doNextPage(true)
          // this.announceCurrentContext()
          event.preventDefault()
          break
        case KeyCodes.ARROW_LEFT:
          this.doPrev()
          // this.announceActiveItem()
          event.preventDefault()
          event.stopPropagation()
          break
        case KeyCodes.ARROW_UP:
          this.doPrevChunk()
          // this.announceActiveItem()
          event.preventDefault()
          event.stopPropagation()
          break
        case KeyCodes.ARROW_RIGHT:
          this.doNext()
          // this.announceActiveItem()
          event.preventDefault()
          event.stopPropagation()
          break
        case KeyCodes.ARROW_DOWN:
          this.doNextChunk()
          // this.announceActiveItem()
          event.preventDefault()
          event.stopPropagation()
          break
        case KeyCodes.ENTER:
          if (!this.isOpen) {
            this.openPicker()
          } else if (this.view === 'year') {
            this.selectYear(this.currentDate)
          } else {
            this.selectQuarterByMonth(this.currentDate)
          }
          break
      }
    },
    doPrev () {
      if (this.view === 'year') {
        this.prevYear(1, true)
      } else {
        this.prevQuarter(true)
      }
    },
    doNext () {
      if (this.view === 'year') {
        this.nextYear(1, true)
      } else {
        this.nextQuarter(true)
      }
    },
    doPrevChunk () {
      if (this.view === 'year') {
        let years = this.chunkSize
        const isFirstChunk = this.currentYear - this.chunkSize < this.rangeStartYear
        if (isFirstChunk && this.yearRange % this.chunkSize !== 0) {
          const diffToStart = this.currentYear - this.rangeStartYear
          years = diffToStart === 0 ? 1 : this.chunkSize + 1
        }
        this.prevYear(years, true)
      } else {
        this.prevQuarter(true)
      }
    },
    doNextChunk () {
      if (this.view === 'year') {
        let years = this.chunkSize
        const isLastChunk = this.currentYear + this.chunkSize > this.rangeEndYear
        if (isLastChunk && this.yearRange % this.chunkSize !== 0) {
          const diffToEnd = this.rangeEndYear - this.currentYear
          years = diffToEnd === 0 ? 1 : this.chunkSize + 1
        }
        this.nextYear(years, true)
      } else {
        this.nextQuarter(true)
      }
    },
    doPrevPage (commitSelection = false) {
      this.prevYear(this.view === 'year' ? this.yearRange : 1, commitSelection)
    },
    doNextPage (commitSelection = false) {
      this.nextYear(this.view === 'year' ? this.yearRange : 1, commitSelection)
    },
    goPrevPage () {
      this.doPrevPage(false)
    },
    goNextPage () {
      this.doNextPage(false)
    },
    prevYear (years = 1, commitSelection = false) {
      const currentDate = this.currentDate || this.today
      const prevDate = currentDate.clone().subtract(years, 'years')
      if (this.isDisabledYear(prevDate)) {
        return
      }
      this.currentDate = prevDate
      if (commitSelection) {
        this.selectedDate = prevDate.clone().startOf('quarter')
      }
    },
    nextYear (years = 1, commitSelection = false) {
      const currentDate = this.currentDate || this.today
      const nextDate = currentDate.clone().add(years, 'years')
      if (this.isDisabledYear(nextDate)) {
        return
      }
      this.currentDate = nextDate
      if (commitSelection) {
        this.selectedDate = nextDate.clone().startOf('quarter')
      }
    },
    prevQuarter (commitSelection = false) {
      const currentDate = this.currentDate || this.today
      const prevQuarter = currentDate.clone().subtract(1, 'quarters')
      if (this.isDisabledMonth(prevQuarter)) {
        return
      }
      this.currentDate = prevQuarter
      if (commitSelection) {
        this.selectedDate = prevQuarter.clone().startOf('quarter')
      }
    },
    nextQuarter (commitSelection = false) {
      const currentDate = this.currentDate || this.today
      const nextQuarter = currentDate.clone().add(1, 'quarters')
      if (this.isDisabledMonth(nextQuarter)) {
        return
      }
      this.currentDate = nextQuarter
      if (commitSelection) {
        this.selectedDate = nextQuarter.clone().startOf('quarter')
      }
    },
    selectYear (year) {
      if (!year) return
      if (!moment.isMoment(year)) year = moment().year(year).startOf('year')
      if (this.isDisabledYear(year)) {
        this.announceAccessible(this.$t('aria.quarter_picker.year_disabled', { year: year.year() }))
        return
      }

      const currentDate = this.currentDate || this.today
      this.currentDate = currentDate.clone().year(year.year())
      this.view = 'quarter'
      this.announceAccessible(this.$t('aria.quarter_picker.year_selected_switch_quarter', { year: year.year() }))
    },
    selectQuarterByMonth (month) {
      if (!month) return
      const currentDate = this.currentDate || this.today
      if (!moment.isMoment(month)) month = moment().year(currentDate.year()).month(month).startOf('month')
      const quarterStartMonth = month.clone().startOf('quarter')
      if (this.isDisabledMonth(quarterStartMonth)) {
        this.announceAccessible(this.$t('aria.quarter_picker.quarter_disabled', {
          quarterLabel: this.getAccessibleQuarterLabel(quarterStartMonth)
        }))
        return
      }

      this.currentDate = quarterStartMonth
      this.selectedDate = quarterStartMonth

      this.announceAccessible(this.$tc('aria.quarter_picker.quarter_selected', 1, {
        quarterLabel: this.getAccessibleQuarterLabel(this.currentDate)
      }))
      this.closePicker()
    },
    openPicker () {
      if (this.isOpen || this.loading || this.disabled) return
      this.view = 'quarter'
      this.currentDate = this.selectedDate ? this.selectedDate.clone() : this.today.clone().startOf('quarter')
      this.isOpen = true
      this.ariaHidden = true
      this.applyPopoverTabIndex()
    },
    closePicker () {
      if (!this.isOpen) return
      this.applyPopoverTabIndex()
      this.isOpen = false
      this.ariaHidden = true
    },
    chunkArray (array, chunkSize) {
      const chunks = []
      for (let i = 0; i < array.length; i += chunkSize) {
        chunks.push(array.slice(i, i + chunkSize))
      }
      return chunks
    },
    isCurrentYear (year) {
      if (!year || !this.selectedDate) return false
      if (!moment.isMoment(year)) year = moment().year(year).startOf('year')
      return this.selectedDate.isSame(year, 'year')
    },
    isCurrentMonth (month) {
      if (!month || !this.selectedDate) return false
      if (!moment.isMoment(month)) month = moment().year(this.currentYear).month(month).startOf('month')
      return this.selectedDate.isSame(month, 'month')
    },
    isCurrentQuarter (chunk) {
      if (!chunk?.[0] || !this.selectedDate) return false
      return this.isCurrentMonth(chunk[0])
    },
    isTodayYear (year) {
      if (!year || !this.today) return false
      if (!moment.isMoment(year)) year = moment().year(year).startOf('year')
      return this.today.isSame(year, 'year')
    },
    isTodayMonth (month) {
      if (!month || !this.today) return false
      if (!moment.isMoment(month)) month = moment().year(this.currentYear).month(month).startOf('month')
      return this.today.isSame(month, 'month')
    },
    isStartDate (month) {
      if (!month || !this.selectedDate) return false
      if (!moment.isMoment(month)) month = moment().year(this.currentYear).month(month).startOf('month')
      return month.isSame(this.selectedDate.clone().startOf('quarter'), 'month')
    },
    isEndDate (month) {
      if (!month || !this.selectedDate) return false
      if (!moment.isMoment(month)) month = moment().year(this.currentYear).month(month).startOf('month')
      return month.isSame(this.selectedDate.clone().endOf('quarter'), 'month')
    },
    isInRange (month) {
      if (!month || !this.selectedDate) return false
      if (!moment.isMoment(month)) month = moment().year(this.currentYear).month(month).startOf('month')
      return month.isBetween(this.selectedDate.clone().startOf('quarter'), this.selectedDate.clone().endOf('quarter'), 'month', '[]')
    },
    isDisabledYear (year) {
      if (typeof this.disabledDate !== 'function') return false

      const targetYear = moment.isMoment(year)
        ? year.clone().startOf('year')
        : moment().year(year).startOf('year')

      const startOfYear = targetYear.clone().startOf('year')
      const dayCount = targetYear.isLeapYear() ? 366 : 365

      for (let i = 0; i < dayCount; i++) {
        const day = startOfYear.clone().add(i, 'day')
        if (!this.disabledDate(day.toDate())) {
          return false
        }
      }

      return true
    },
    isDisabledMonth (month) {
      if (typeof this.disabledDate !== 'function') return false

      const targetMonth = moment.isMoment(month)
        ? month.clone().startOf('month')
        : moment().year(this.currentYear).month(month).startOf('month')

      const startOfMonth = targetMonth.clone().startOf('month')
      const dayCount = targetMonth.daysInMonth()

      for (let i = 0; i < dayCount; i++) {
        const day = startOfMonth.clone().add(i, 'day')
        if (!this.disabledDate(day.toDate())) {
          return false
        }
      }

      return true
    }
  }
}
</script>

<style scoped>
.el-date-table .el-date-table__row.current div {
    background-color: #f2f6fc;
}
.el-date-table td.end-date div, .el-date-table td.start-date div {
    color: #fff;
}
.el-date-table td.start-date div {
    margin-left: 5px;
    border-top-left-radius: 15px;
    border-bottom-left-radius: 15px;
}
.el-date-table.is-week-mode .el-date-table__row.hovered div {
    background-color: #f2f6fc;
}
.el-date-table.is-week-mode .el-date-table__row.hovered td:first-child div {
    margin-left: 5px;
    border-top-left-radius: 15px;
    border-bottom-left-radius: 15px;
}
.el-date-table.is-week-mode .el-date-table__row.hovered td:last-child div {
    margin-right: 5px;
    border-top-right-radius: 15px;
    border-bottom-right-radius: 15px;
}

.el-year-table td .cell.hovered, .el-month-table .el-date-table__row.hovered:not(:has(.cell:hover)) td:nth-child(2) .cell {
    color: #0f699d;
}

::v-deep .el-date-picker {
  padding: 0;
  width: auto;
}
.el-date-picker .el-picker-panel__content {
  width: 242px;
}

.quarter-picker:has(input:focus)::v-deep .el-input__inner {
  outline: 0;
}
.quarter-picker:has(input:focus)::v-deep div:not(.static-css) > .el-input__inner {
  border-color: var(--primary-DEFAULT);
}
</style>
